const REVIEW_TYPE_PREVIEW = "preview";

const parseMaybeJsonValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const getObjectPropCI = (obj, ...names) => {
  if (!obj || typeof obj !== "object") return undefined;
  const lookup = new Map(Object.keys(obj).map((key) => [key.toLowerCase(), key]));
  for (const name of names) {
    const key = lookup.get(String(name).toLowerCase());
    if (key !== undefined) return obj[key];
  }
  return undefined;
};

const normalizePreviewUiCommandValue = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "display") return "data";
  return normalized;
};

export const isWarehouseQuestionOperation = (operationName) =>
  String(operationName ?? "").trim().toLowerCase() === "aiquestionwarehouse";

export const isPermissionSetOperation = (operationName) => {
  const normalized = String(operationName ?? "").trim().toLowerCase();
  return normalized === "aipermissionset" || normalized === "aiquestionpermissionset";
};

export const isPreviewRefreshAvailable = ({
  reviewDraft,
  operationName,
  previewRefreshEnabled
}) => {
  if (!reviewDraft || reviewDraft.kind !== REVIEW_TYPE_PREVIEW) return false;
  if (previewRefreshEnabled === false) return false;

  const previewCommand = normalizePreviewUiCommandValue(reviewDraft.finalUiCommand);
  if (!["data", "render", "chart"].includes(previewCommand)) return false;

  if (previewRefreshEnabled === true) return true;

  return isWarehouseQuestionOperation(operationName);
};

const getPermissionEntryId = (entry) =>
  String(
    getObjectPropCI(
      entry,
      "Permission",
      "permission",
      "PermissionId",
      "permissionId",
      "Id",
      "id"
    ) ?? ""
  ).trim();

const looksLikePermissionEntry = (value) =>
  Boolean(value && typeof value === "object" && getPermissionEntryId(value));

const extractPermissionEntries = (value, depth = 0, seen = new Set()) => {
  const parsed = parseMaybeJsonValue(value);
  if (depth > 8 || parsed == null) return [];

  if (Array.isArray(parsed)) {
    return parsed.filter(looksLikePermissionEntry);
  }

  if (typeof parsed !== "object") return [];
  if (seen.has(parsed)) return [];
  seen.add(parsed);

  if (looksLikePermissionEntry(parsed)) return [parsed];

  const directKeys = [
    "Entries",
    "entries",
    "PermissionSets",
    "permissionSets",
    "Permissions",
    "permissions"
  ];

  for (const key of directKeys) {
    const candidate = getObjectPropCI(parsed, key);
    const entries = extractPermissionEntries(candidate, depth + 1, seen);
    if (entries.length) return entries;
  }

  const nestedKeys = [
    "payload",
    "Payload",
    "output",
    "Output",
    "data",
    "Data",
    "value",
    "Value",
    "permissionSet",
    "PermissionSet"
  ];

  for (const key of nestedKeys) {
    const candidate = getObjectPropCI(parsed, key);
    const entries = extractPermissionEntries(candidate, depth + 1, seen);
    if (entries.length) return entries;
  }

  return [];
};

const buildPermissionDisplayMap = (reviewContext) => {
  const catalog =
    getObjectPropCI(
      reviewContext,
      "permissions",
      "Permissions",
      "permissionCatalog",
      "PermissionCatalog"
    ) ?? [];

  return (Array.isArray(catalog) ? catalog : []).reduce((map, item) => {
    const id = getPermissionEntryId(item) || String(item?.Permission ?? item?.permission ?? "").trim();
    if (!id) return map;
    const displayName = String(
      getObjectPropCI(item, "DisplayName", "displayName", "Name", "name") ?? id
    ).trim();
    map.set(id.toLowerCase(), displayName || id);
    return map;
  }, new Map());
};

export const resolvePermissionPreviewAdditions = ({ previewValue, basePayload, reviewContext }) => {
  const previewEntries = extractPermissionEntries(previewValue);
  if (!previewEntries.length) return null;

  const basePermissionIds = new Set(
    extractPermissionEntries(basePayload)
      .map(getPermissionEntryId)
      .filter(Boolean)
      .map((id) => id.toLowerCase())
  );
  const displayMap = buildPermissionDisplayMap(reviewContext);
  const seen = new Set();

  return previewEntries.reduce((result, entry) => {
    const id = getPermissionEntryId(entry);
    const key = id.toLowerCase();
    if (!id || basePermissionIds.has(key) || seen.has(key)) return result;
    seen.add(key);
    result.push(displayMap.get(key) ?? id);
    return result;
  }, []);
};
